import I18n from 'i18n-js';
import React, { useRef, useContext, useState } from 'react';
import { SparkleIcon } from '@phosphor-icons/react';

import { isZeroAmount, parseAmount } from '@reverbdotcom/commons/src/money';
import { RCCallout, RCRadioCardGroup, RCAlertBox, RCTextWithIcon, RCIcon } from '@reverbdotcom/cadence/components';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { WarningIcon } from '@reverbdotcom/cadence/icons/react';
import { US_CON, US, ELECTRIC_GUITARS_UUID, ACOUSTIC_GUITARS_UUID } from '@reverbdotcom/commons/src/constants';
import { FormCards } from '@reverbdotcom/commons/src/components/FormCards';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';

import { Field, ActionType } from '../../sellFormReducerTypes';
import { SellFormContext } from '../../SellFormContext';
import EditShippingRateCardManualPrices from '../../../../shared/edit_shipping_rate_card_manual_prices';
import CarrierCalculatedFormContainer from './CarrierCalculatedFormContainer';
import GuitarPackagingTipsCallout from './GuitarPackagingTipsCallout';
import URLHelpers from '../../../../shared/url_helpers';
import {
  upsertShippingRate,
  BLANK_RATE_ATTRS,
  buildBlankShippingRate,
  FREE_RATE_ATTRS,
  CARRIER_CALC_RATE_TYPE,
} from './ShippingForm';
import CarrierCalcAddOns from './CarrierCalcAddOns';
import CarrierCalcPickupStore from './CarrierCalcPickupStore';

export enum ShippingMode {
  CUSTOM = 'custom', // user manually inputs a standard shipping price (with optional combine & save price)
  CARRIER_CALCULATED = 'carrierCalculated', // user inputs package dimensions, reverb figures out the shipping cost
  FREE = 'free', // user selects simple free shipping (no combine + save; no user input required)
}

const CARRIER_CALCULATED_LISTING_PRICE_LIMIT_USD = 10000;
const LABEL_ADJUSTMENTS_HC_ARTICLE_ID = '41988405274395';

function getLocalizedRegionName(regionCode: string): string {
  if (regionCode === US_CON) {
    return I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.theContinentalUs');
  } else {
    return I18n.t(`discovery.shippingRegions.${regionCode}`);
  }
}

function DomesticShippingRadios() {
  const { listingEntry, dispatch, shopConfig, loading } = useContext(SellFormContext);
  const labelAdjustmentsHcArticleUrl = useHelpCenterArticleUrl(LABEL_ADJUSTMENTS_HC_ARTICLE_ID);

  const hasGuitarRootCategory = [ELECTRIC_GUITARS_UUID, ACOUSTIC_GUITARS_UUID].includes(listingEntry[Field.ROOT_CATEGORY_UUID]);
  const { currencySetting, shippingRegionSettings, address } = shopConfig;
  const domesticRegion = shippingRegionSettings.find(({ isDomestic }) => isDomestic);
  const listingPrice = parseAmount(listingEntry[Field.PRICE]?.amount, 'USD').amountCents / 100.00;
  const priceAmountWithinCCLimits = listingPrice < CARRIER_CALCULATED_LISTING_PRICE_LIMIT_USD;
  const inCarrierCalculatedMode = !!listingEntry[Field.CARRIER_CALCULATED_PACKAGE];
  const carrierCalculatedSupported = domesticRegion.allowedToShipCarrierCalculated && priceAmountWithinCCLimits;
  const shippingRate = listingEntry[Field.SHIPPING_RATES].find((sr) => sr.regionCode === domesticRegion.regionCode);
  const localizedRegionName = getLocalizedRegionName(domesticRegion.regionCode);
  const isUSCountryCode = address.countryCode === US;
  const accessPointShippingEnabled = useExpEnabled(experiments.ALLOW_ACCESS_POINT_SHIPPING);

  const headerRef = useRef<HTMLInputElement>();
  const [touchedRadio, setTouchedRadio] = useState(!!shippingRate?.rate?.amount);
  const [isFreeModeSelected, setIsFreeModeSelected] = useState(
    isZeroAmount(shippingRate?.rate?.amount) &&
    !shippingRate?.incrementalRate?.amount &&
    !shippingRate?.expeditedRate?.amount,
  );
  const shippingModes = carrierCalculatedSupported ?
    [ShippingMode.CARRIER_CALCULATED, ShippingMode.FREE, ShippingMode.CUSTOM] :
    [ShippingMode.FREE, ShippingMode.CUSTOM];

  let currentMode;
  if (carrierCalculatedSupported && inCarrierCalculatedMode) {
    currentMode = ShippingMode.CARRIER_CALCULATED;
  } else if (isFreeModeSelected) {
    currentMode = ShippingMode.FREE;
  } else if (shippingRate?.rate?.amount || touchedRadio) {
    currentMode = ShippingMode.CUSTOM;
  }

  function toggleCarrierCalculated() {
    if (inCarrierCalculatedMode) {
      dispatch({
        type: ActionType.DEACTIVATE_CARRIER_CALCULATED_MODE,
        shippingRates: upsertShippingRate(listingEntry[Field.SHIPPING_RATES], buildBlankShippingRate(shippingRate.regionCode)),
      });
    } else {
      dispatch({
        type: ActionType.ACTIVATE_CARRIER_CALCULATED_MODE,
        shippingRates: upsertShippingRate(
          listingEntry[Field.SHIPPING_RATES],
          buildBlankShippingRate(shippingRate.regionCode, CARRIER_CALC_RATE_TYPE),
        ),
      });
    }
  }

  function handleModeChange(newMode) {
    setTouchedRadio(true);
    switch (newMode) {
      case ShippingMode.CARRIER_CALCULATED:
        toggleCarrierCalculated();
        setIsFreeModeSelected(false);
        break;

      case ShippingMode.CUSTOM:
        if (inCarrierCalculatedMode) { toggleCarrierCalculated(); }
        if (isFreeModeSelected) {
          setIsFreeModeSelected(false);
          dispatch({
            type: ActionType.UPDATE_FIELD,
            fieldKey: Field.SHIPPING_RATES,
            value: upsertShippingRate(listingEntry[Field.SHIPPING_RATES], { ...shippingRate, ...BLANK_RATE_ATTRS }),
          });
        }
        break;

      case ShippingMode.FREE:
        if (inCarrierCalculatedMode) { toggleCarrierCalculated(); }
        setIsFreeModeSelected(true);
        dispatch({
          type: ActionType.UPDATE_FIELD,
          fieldKey: Field.SHIPPING_RATES,
          value: upsertShippingRate(listingEntry[Field.SHIPPING_RATES], { ...shippingRate, ...FREE_RATE_ATTRS }),
        });
        break;

      default:
        break;
    }
  }

  // Edge case warning for persisted listings that - for historical reasons - don't have a domestic rate.
  if (!shippingRate) {
    return (
      <FormCards.Card>
        <RCAlertBox type="warning" small>
          <RCTextWithIcon icon={<WarningIcon />} placement="left">
            <Translate
              text="discovery.sellForm.shippingSection.domesticShippingRadios.missingDomesticWarning"
              args={{ regionName: localizedRegionName }}
            >
              <button
                className="button-as-link ml-1"
                type="button"
                onClick={() => {
                  dispatch({
                    type: ActionType.UPDATE_FIELD,
                    fieldKey: Field.SHIPPING_RATES,
                    value: upsertShippingRate(listingEntry[Field.SHIPPING_RATES], buildBlankShippingRate(domesticRegion.regionCode)),
                  });
                }}
              >
                {I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.missingDomesticButtonText')}
              </button>
            </Translate>
          </RCTextWithIcon>
        </RCAlertBox>
      </FormCards.Card>
    );
  }

  return (
    <>
      <FormCards.Card>
        <div ref={headerRef} />
        <FormCards.FormSection
          title={I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.headerV2', { regionName: localizedRegionName })}
          tag="required"
          wrapperHtmlTag="fieldset"
          fullWidthForm
        >
          <RCRadioCardGroup
            label={I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.headerV2', { regionName: localizedRegionName })}
            id={`howhandle_${shippingRate.regionCode}`}
            name={`howhandle_${shippingRate.regionCode}`}
            value={currentMode}
            onChange={(evt) => handleModeChange(evt.target.value)}
            visuallyHiddenLabel
            required
          >
            {shippingModes.map((mode) => (
              <RCRadioCardGroup.Option
                key={mode}
                value={mode}
                label={I18n.t(`discovery.sellForm.shippingSection.domesticShippingRadios.label.${mode}`)}
                customTag={mode === ShippingMode.CARRIER_CALCULATED && { variant: 'highlight', label: I18n.t('commons.formGroup.recommended') }}
                sublabel={(mode === currentMode || mode === ShippingMode.CARRIER_CALCULATED) && (
                  <>
                    <div>
                      {I18n.t(`discovery.sellForm.shippingSection.domesticShippingRadios.subLabel.${mode}`)}

                      {mode === ShippingMode.CARRIER_CALCULATED && (
                        <CoreLink
                          style={{ zIndex: '1', position: 'relative' }}
                          className="ml-1 text-link"
                          to={URLHelpers.legalPagePath('calculated-shipping-terms-and-conditions')}
                          target="_blank"
                        >
                          {I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.carrierCalcTerms')}
                        </CoreLink>
                      )}
                    </div>

                    {mode === ShippingMode.CARRIER_CALCULATED && (
                      <div className="mt-2">
                        <RCCallout
                          themeType="utility"
                          variant="neutral-ultralight"
                          title={I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.carrierCalcValueBanner.title')}
                          icon={(
                            <span className="text-color-vu-orange-600">
                              <RCIcon icon={<SparkleIcon />} size={10} />
                            </span>
                          )}
                          compact
                        >
                          {I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.carrierCalcValueBanner.content')}
                        </RCCallout>
                      </div>
                    )}
                  </>
                )}
              />
            ))}
          </RCRadioCardGroup>
        </FormCards.FormSection>
      </FormCards.Card>

      {hasGuitarRootCategory && isUSCountryCode &&
        <GuitarPackagingTipsCallout />
      }

      {[ShippingMode.CARRIER_CALCULATED, ShippingMode.CUSTOM].includes(currentMode) && (
        <FormCards.Card>
          <FormCards.FormSection
            title={I18n.t(
              `discovery.sellForm.shippingSection.domesticShippingRadios.detailsCard.title.${currentMode === ShippingMode.CUSTOM ? currentMode : currentMode + 'PackageDimensions'}`,
              { regionName: localizedRegionName },
            )}
            subtitle={currentMode === ShippingMode.CARRIER_CALCULATED &&
              <span>
                {I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.detailsCard.carrierCalculatedAccurateMeasurementsSubtitle')}
                <CoreLink
                  style={{ zIndex: '1', position: 'relative' }}
                  className="ml-1 text-link"
                  to={`${labelAdjustmentsHcArticleUrl}#Avoid`}
                  target="_blank"
                >
                  {I18n.t('discovery.sellForm.shippingSection.domesticShippingRadios.detailsCard.carrierCalculatedSubtitleTips')}
                </CoreLink>
              </span>
            }
            tag="required"
            fullWidthForm
          >
            {currentMode === ShippingMode.CARRIER_CALCULATED && <CarrierCalculatedFormContainer />}

            {currentMode === ShippingMode.CUSTOM && (
              <EditShippingRateCardManualPrices
                shippingRate={shippingRate}
                handleEditShippingRate={(updated) => {
                  dispatch({
                    type: ActionType.UPDATE_FIELD,
                    fieldKey: Field.SHIPPING_RATES,
                    value: upsertShippingRate(listingEntry[Field.SHIPPING_RATES], updated),
                  });
                }}
                currency={currencySetting}
                allowedToShipExpedited={false}
                disabled={loading}
                displayShippingEstimator={isUSCountryCode}
                handleCarrierCalcNudgeClick={carrierCalculatedSupported ? () => handleModeChange(ShippingMode.CARRIER_CALCULATED) : undefined}
              />
            )}
          </FormCards.FormSection>
        </FormCards.Card>
      )}

      {currentMode === ShippingMode.CARRIER_CALCULATED && accessPointShippingEnabled && <CarrierCalcPickupStore />}

      <CarrierCalcAddOns />
    </>
  );
}

export default DomesticShippingRadios;
